<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const event = inject('tiqCurrentEvent', null);
// A visitor never meets the slot's placeholder: with no real event
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

const issues = computed(() => (event && Array.isArray(event.maintenance_issues) ? event.maintenance_issues : []));
// `||` rather than `??`, matching the Classic twin: a blank heading
// falls back to the default under both templates, so switching
// template cannot change what the section is called.
const heading = computed(() => props.config.heading || "What we'll be working on");
const subtitle = computed(() => props.config.subtitle || '');

// The severity chip wears the design's hazard colors rather than the
// dashboard's, so the row reads as part of this page. The vocabulary
// is the same four words a manager sees in Maintenance.
const PRIORITY_CHIPS = {
    critical: { bg: '#d2452f', fg: '#ffffff' },
    high: { bg: '#f8e2df', fg: '#9b2a1f' },
    medium: { bg: '#fbf0d9', fg: '#7d5200' },
    low: { bg: '#e3f1e6', fg: '#1d6b35' },
};
const PRIORITY_LABELS = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
function priorityChip(p) { return PRIORITY_CHIPS[p] || { bg: '#e8ecef', fg: '#3b4a57' }; }
function priorityLabel(p) { return PRIORITY_LABELS[p] || String(p ?? ''); }

const STATUS_LABELS = {
    open: 'Open',
    in_progress: 'In progress',
    pending_review: 'Pending review',
    resolved: 'Resolved',
};
function statusText(s) { return STATUS_LABELS[s] || String(s ?? 'Open').replace('_', ' '); }
</script>

<template>
    <section v-if="event ? issues.length > 0 : editing"
             :id="config.anchor_id || null"
             class="th-block th-sec th-emaint"
             :style="styleVars(config)">
        <div class="th-wrap th-emaint__wrap">
            <header v-if="heading || subtitle" class="th-emaint__head">
                <h2 v-if="heading" class="th-h2 th-h2--sm">{{ heading }}</h2>
                <p v-if="subtitle" class="th-body th-emaint__sub">{{ subtitle }}</p>
            </header>

            <p v-if="!event" class="th-emaint__empty">
                Linked trail work appears here when the template renders a real event.
            </p>

            <div v-else class="th-panel th-emaint__list">
                <div v-for="i in issues" :key="i.id" class="th-emaint__row">
                    <span v-if="i.priority"
                          class="th-emaint__sev"
                          :style="{ background: priorityChip(i.priority).bg, color: priorityChip(i.priority).fg }">
                        {{ priorityLabel(i.priority) }}
                    </span>
                    <div class="th-emaint__main">
                        <p class="th-emaint__title">
                            <span v-if="i.issue_type" class="th-emaint__type">{{ i.issue_type }}</span>
                            <span v-if="i.issue_type && i.trail_name" class="th-emaint__sep"> &middot; </span>
                            <a v-if="i.trail_name && i.trail_href" class="th-emaint__trail" :href="i.trail_href">{{ i.trail_name }}</a>
                            <span v-else-if="i.trail_name">{{ i.trail_name }}</span>
                        </p>
                        <p class="th-emaint__name">{{ i.title }}</p>
                        <p v-if="i.description" class="th-emaint__desc">{{ i.description }}</p>
                    </div>
                    <span class="th-mono th-mono--caps th-emaint__status">{{ statusText(i.status) }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-emaint__wrap { max-width: 1000px; }
.th-emaint__head { margin-bottom: 20px; }
.th-emaint__sub { margin-top: 10px; max-width: 60ch; }
.th-emaint__list { display: flex; flex-direction: column; }
.th-emaint__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px 20px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--th-line-soft);
}
.th-emaint__row:last-child { border-bottom: 0; }
.th-emaint__sev {
    flex: none;
    min-width: 72px;
    text-align: center;
    padding: 5px 9px;
    border-radius: 3px;
    font: 500 11px/1.2 var(--th-mono);
    letter-spacing: .08em;
    text-transform: uppercase;
}
.th-emaint__main { flex: 1; min-width: 220px; display: flex; flex-direction: column; gap: 3px; }
.th-emaint__title { margin: 0; font: 500 11px/1.3 var(--th-mono); letter-spacing: .08em; text-transform: uppercase; color: var(--th-meta); }
.th-emaint__trail { border-bottom: 1px solid var(--accent); }
.th-emaint__name { margin: 0; font-weight: 600; font-size: 16px; }
.th-emaint__desc { margin: 0; font-size: 15px; color: var(--text-sub, var(--th-muted)); }
.th-emaint__status { flex: none; padding-top: 4px; }
.th-emaint__empty {
    margin: 0;
    padding: 28px;
    text-align: center;
    font-size: 15px;
    color: var(--th-faint);
    border: 1px dashed var(--th-field);
    border-radius: var(--r);
    background: rgba(0, 0, 0, .02);
}
</style>
